import React, { lazy, Suspense } from 'react';
import { Card, CardContent } from '../ui/card';
import { Loader2 } from 'lucide-react';
import PremiumToolLock, { isPremiumTool } from '../PremiumToolLock';
import usePremiumGate from '../../hooks/usePremiumGate';
import { useLanguage } from '../../contexts/LanguageContext';
import { LS } from '../../utils/lang';

// Lazy load all study tool components
const BibleReader = lazy(() => import('../BibleReader'));
const AIInsights = lazy(() => import('../AIInsights'));
const ParallelView = lazy(() => import('../ParallelView'));
const ConcordanceTool = lazy(() => import('../ConcordanceTool'));
const CommentaryTool = lazy(() => import('../CommentaryTool'));
const NotesPanel = lazy(() => import('../NotesPanel'));
const WordStudyPanel = lazy(() => import('../WordStudyPanel'));
const InterlinearView = lazy(() => import('../InterlinearView'));
const CrossReferencesPanel = lazy(() => import('../CrossReferencesPanel'));
const VerseComparisonTool = lazy(() => import('../VerseComparisonTool'));
const BookmarksPanel = lazy(() => import('../BookmarksPanel'));
const BibleDictionary = lazy(() => import('../BibleDictionary'));
const BibleMaps = lazy(() => import('../BibleMaps'));
const DevotionalTool = lazy(() => import('../DevotionalTool'));
const GospelHarmonyTool = lazy(() => import('../GospelHarmonyTool'));
const CharacterEncyclopedia = lazy(() => import('../CharacterEncyclopedia'));
const AICompanion = lazy(() => import('../AICompanion'));
const LumeraCompanion = lazy(() => import('../LumeraCompanion'));
const UserProfile = lazy(() => import('../UserProfile'));
const VerseOfTheDay = lazy(() => import('../VerseOfTheDay'));
const ReadingPlansView = lazy(() => import('../ReadingPlansView'));
const HighlightsViewer = lazy(() => import('../HighlightsViewer'));
const VerseImageGenerator = lazy(() => import('../VerseImageGenerator'));
const NotificationSettings = lazy(() => import('../NotificationSettings'));
const VideosHub = lazy(() => import('../VideosHub'));
const TopicalBiblePane = lazy(() => import('../TopicalBiblePane'));
const BibleAtlasPane = lazy(() => import('../BibleAtlasPane'));
const HistoricReadingPlansPane = lazy(() => import('../HistoricReadingPlansPane'));
const ApocryphaPane = lazy(() => import('../ApocryphaPane'));
const OriginalLanguagesPane = lazy(() => import('../OriginalLanguagesPane'));
const ISBEEncyclopediaPane = lazy(() => import('../ISBEEncyclopediaPane'));
const ChronologyExplorer = lazy(() => import('../ChronologyExplorer'));

/**
 * Loading fallback component
 */
const LoadingFallback = ({ paneId }) => (
  <Card className="h-full">
    <CardContent className="flex items-center justify-center h-full min-h-[300px]">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto mb-3" />
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {LS('Loading…', 'Cargando…')}
        </p>
      </div>
    </CardContent>
  </Card>
);

/**
 * Lazy Pane Renderer Component
 * Renders components with lazy loading and suspense
 */
const LazyPaneRenderer = ({ 
  paneId, 
  selectedVerse, 
  aiInsights, 
  showAIInsights,
  onVerseChange, 
  onAIAnalyze, 
  isLoadingAI 
}) => {
  const { isPremium } = usePremiumGate();
  const { t } = useLanguage();
  const renderPane = () => {
    // Lumera+ tools stay locked inside the dashboard too (not just as standalone pages).
    if (isPremiumTool(paneId) && !isPremium) {
      return <PremiumToolLock toolId={paneId} title={t(`tools.${paneId}`)} />;
    }
    switch (paneId) {
      case 'bible':
        return (
          <BibleReader 
            initialVerse={selectedVerse} 
            onVerseChange={onVerseChange} 
            onAIAnalyze={onAIAnalyze} 
            isLoadingAI={isLoadingAI} 
          />
        );
      case 'votd':
        return <VerseOfTheDay />;
      case 'companion':
        return <LumeraCompanion />;
      case 'reading-plans':
        return <ReadingPlansView />;
      case 'highlights':
        return <HighlightsViewer />;
      case 'verse-image':
        return <VerseImageGenerator initialVerse={selectedVerse} />;
      case 'parallel':
        return (
          <ParallelView 
            reference={`${selectedVerse.book} ${selectedVerse.chapter}:${selectedVerse.verse}`} 
          />
        );
      case 'comparison':
        return <VerseComparisonTool initialReference={selectedVerse} />;
      case 'interlinear':
        return <InterlinearView reference={selectedVerse.reference} />;
      case 'concordance':
        return <ConcordanceTool currentVerse={selectedVerse} />;
      case 'commentary':
        return <CommentaryTool currentVerse={selectedVerse} />;
      case 'dictionary':
        return <BibleDictionary />;
      case 'characters':
        return <CharacterEncyclopedia />;
      case 'devotional':
        return <DevotionalTool />;
      case 'harmony':
        return <GospelHarmonyTool />;
      case 'maps':
        return <BibleMaps />;
      case 'chronology':
        return <ChronologyExplorer />;
      case 'notes':
        return <NotesPanel currentVerse={selectedVerse} />;
      case 'wordstudy':
        return <WordStudyPanel />;
      case 'crossrefs':
        return <CrossReferencesPanel currentVerse={selectedVerse} />;
      case 'bookmarks':
        return <BookmarksPanel />;
      case 'profile':
        return <UserProfile />;
      case 'notifications':
        return <NotificationSettings />;
      case 'videos':
        return <VideosHub embedded />;
      case 'topical':
        return <TopicalBiblePane />;
      case 'atlas':
        return <BibleAtlasPane />;
      case 'historic-plans':
        return <HistoricReadingPlansPane />;
      case 'apocrypha':
        return <ApocryphaPane />;
      case 'original-languages':
        return <OriginalLanguagesPane />;
      case 'isbe':
        return <ISBEEncyclopediaPane />;
      case 'ai':
        return showAIInsights && aiInsights ? (
          <AIInsights verse={selectedVerse} insights={aiInsights} />
        ) : (
          <Card>
            <CardContent className="py-12 text-center text-slate-700 dark:text-slate-400">
              {LS('Click AI Insights on a verse', 'Toca Perspectivas IA en un versículo')}
            </CardContent>
          </Card>
        );
      default:
        return null;
    }
  };

  return (
    <Suspense fallback={<LoadingFallback paneId={paneId} />}>
      {renderPane()}
    </Suspense>
  );
};

export default LazyPaneRenderer;
